import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/hooks/useLanguage';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Video, Phone, MapPin } from 'lucide-react';

interface AppointmentStepProps {
  form: UseFormReturn<any>;
}

const AppointmentStep = ({ form }: AppointmentStepProps) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2">{t('booking.appointmentType')}</h3>
        <p className="text-muted-foreground">{t('booking.chooseDateTime')}</p>
      </div>

      <FormField
        control={form.control}
        name="appointmentType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('booking.appointmentType')} *</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="In-person" id="in-person" />
                      <label 
                        htmlFor="in-person" 
                        className="flex items-center gap-2 cursor-pointer font-medium"
                      >
                        <MapPin className="h-4 w-4" />
                        {t('booking.inPerson')}
                      </label>
                    </div>
                  </FormControl>
                </FormItem>
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Phone" id="phone" />
                      <label 
                        htmlFor="phone" 
                        className="flex items-center gap-2 cursor-pointer font-medium"
                      >
                        <Phone className="h-4 w-4" />
                        {t('booking.phoneCall')}
                      </label>
                    </div>
                  </FormControl>
                </FormItem>
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Video" id="video" />
                      <label 
                        htmlFor="video" 
                        className="flex items-center gap-2 cursor-pointer font-medium"
                      >
                        <Video className="h-4 w-4" />
                        {t('booking.video')}
                      </label>
                    </div>
                  </FormControl>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="preferredDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('booking.preferredDate')} *</FormLabel>
            <FormControl>
              <Input {...field} type="date" required />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="preferredTime"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('booking.preferredTime')} *</FormLabel>
            <FormControl>
              <Input {...field} type="time" required />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="bg-card p-6 rounded-lg border">
        <h4 className="text-sm font-semibold mb-4">📅 {t('booking.viewAvailability')}</h4>
        <p className="text-sm text-muted-foreground mb-4">
          {t('booking.calendarDesc')}
        </p>
        <div className="aspect-video w-full overflow-hidden rounded-lg border bg-muted/50">
          <iframe 
            src="https://calendar.google.com/calendar/embed?src=info%40danielalbertlaw.com&ctz=America%2FChicago&mode=WEEK&showTitle=0&showNav=1&showPrint=0&showTabs=0&showCalendars=0&showTz=0"
            className="w-full h-full"
            frameBorder="0"
            scrolling="no"
            title="Available Appointment Times"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-4 text-center">
          {t('booking.calendarNote')}
        </p>
      </div>
    </div>
  );
};

export default AppointmentStep;
